import { STATUS } from "@/lib/labels";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function StatusBadge({ status }) {
  const s = STATUS[status] || { label: status, cls: "text-zinc-400 bg-zinc-500/10 border-zinc-500/20" };
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

export function TypeBadge({ label }) {
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border text-zinc-300 bg-zinc-500/10 border-zinc-500/20">
      {label}
    </span>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="print-hide flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-zinc-50">{title}</h1>
        {subtitle && <p className="text-sm text-zinc-400 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center justify-end gap-2">{children}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, desc }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-[#1A1A22] rounded-md">
      <Icon className="w-10 h-10 text-zinc-600 mb-3" />
      <p className="text-zinc-300 font-medium">{title}</p>
      {desc && <p className="text-sm text-zinc-500 mt-1">{desc}</p>}
    </div>
  );
}

export function Money({ value, className = "" }) {
  const n = Number(value) || 0;
  return (
    <span className={`font-mono tabular-nums ${n < 0 ? "text-rose-500" : ""} ${className}`}>
      {new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n)}
    </span>
  );
}

export const defaultPeriod = () => ({
  mode: "all",
  month: new Date().toISOString().slice(0, 7),
  year: String(new Date().getFullYear()),
});

export function periodRange(p) {
  if (p.mode === "month" && p.month) {
    const [y, m] = p.month.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    return { start: `${p.month}-01`, end: `${p.month}-${String(last).padStart(2, "0")}` };
  }
  if (p.mode === "year" && p.year) {
    return { start: `${p.year}-01-01`, end: `${p.year}-12-31` };
  }
  return { start: null, end: null };
}

export function PeriodFilter({ value, onChange, testidPrefix = "period" }) {
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => String(thisYear - i));
  return (
    <div className="flex items-center gap-2">
      <Select value={value.mode} onValueChange={(v) => onChange({ ...value, mode: v })}>
        <SelectTrigger data-testid={`${testidPrefix}-mode`} className="w-36 h-10 bg-[#0F0F12] border-[#1A1A22]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
          <SelectItem value="all">Semua Waktu</SelectItem>
          <SelectItem value="month">Per Bulan</SelectItem>
          <SelectItem value="year">Per Tahun</SelectItem>
        </SelectContent>
      </Select>
      {value.mode === "month" && (
        <Input
          data-testid={`${testidPrefix}-month`}
          type="month"
          value={value.month}
          onChange={(e) => onChange({ ...value, month: e.target.value })}
          className="w-40 h-10 bg-[#0F0F12] border-[#1A1A22]"
        />
      )}
      {value.mode === "year" && (
        <Select value={value.year} onValueChange={(v) => onChange({ ...value, year: v })}>
          <SelectTrigger data-testid={`${testidPrefix}-year`} className="w-28 h-10 bg-[#0F0F12] border-[#1A1A22]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
            {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
